import { SECTIONS } from 'chatspot-core/messages/DisplayTypes';

// Keep it consistent with copilot utils file here - https://git.hubteam.com/HubSpot/chatspot-ui/blob/master/chatspot-widget-ui/static/js/chat/utils/copilotV2ResponseUtil.ts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
/**
 * CHIRP RPC wraps the data table rows in a  `value` object. This function
 * unwraps the value object and maps the data table rows to the correct type
 * so we can use the existing DataTable renderer.
 */
const mapCopilotV2DataTableSectionToDataTableSection = section => {
  return Object.assign({}, section, {
    type: 'DATA_TABLE',
    dataTable: Object.assign({}, section.dataTable, {
      data: section.dataTable.data.map(row => {
        return row.value;
      })
    })
  });
};
export const mapCopilotV2MessageTypeToMessageType = messageType => {
  switch (messageType) {
    case 'USER':
      return 'user';
    case 'COPILOT':
      return 'chatspot';
    default:
      throw new Error(`Unknown message type: ${messageType.toString()}`);
  }
};
const mapCopilotV2ActionTypenameToActionType = typename => {
  switch (typename) {
    case 'com.hubspot.chatspot.message.schemas.buttons.ActionButton':
      return 'ACTION';
    case 'com.hubspot.chatspot.message.schemas.buttons.ActionButtonV2':
      return 'ACTION_V2';
    case 'com.hubspot.chatspot.message.schemas.buttons.LinkButton':
      return 'LINK';
    case 'com.hubspot.chatspot.message.schemas.buttons.TemplateButton':
      return 'TEMPLATE';
    default:
      throw new Error(`Unknown action type: ${typename}`);
  }
};
export const mapCopilotV2SectionsToSections = sections => {
  // TODO - @mlegocki: thread-endpoints are returning a diffeerent message schema compared to streaming messages
  // resolve this type issue with BE. We should be getting consistent types for messages in threads vs. streaming messages
  return sections.map(section => {
    if (section.__typename && !section.type) {
      /**
       * Our section renderers expect a specific section type. Currently, there is a issue
       * where the CHIRP endpoint removes the section type so we will map the typename
       * to the corresponding section type.
       */

      switch (section.__typename) {
        case 'com.hubspot.chatspot.message.schemas.sections.UserChatResponseSection':
        case 'com.hubspot.chatspot.message.schemas.sections.UserActionResponseSection':
          return Object.assign({}, section, {
            type: 'PLAIN_TEXT'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.ErrorResponseSection':
          return Object.assign({}, section, {
            type: 'ERROR'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.HtmlTextResponseSection':
          return Object.assign({}, section, {
            type: 'HTML'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.PlainTextResponseSection':
          return Object.assign({}, section, {
            type: 'PLAIN_TEXT'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.DataTableResponseSection':
          return mapCopilotV2DataTableSectionToDataTableSection(section);
        case 'com.hubspot.chatspot.message.schemas.sections.ThoughtResponseSection':
          return Object.assign({}, section, {
            type: 'THOUGHT'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.ActionsResponseSection':
          return Object.assign({}, section, {
            actions: section.actions.map(action => Object.assign({}, action, {
              type: mapCopilotV2ActionTypenameToActionType(action.__typename)
            })),
            type: 'ACTION'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.AccordionResponseSection':
          return Object.assign({}, section, {
            type: 'ACCORDION'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.card.CardResponseSection':
          return Object.assign({}, section, {
            type: 'CARD'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.ConfirmationResponseSection':
          return Object.assign({}, section, {
            type: 'CONFIRMATION'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.image.ImageGridResponseSection':
          return Object.assign({}, section, {
            type: 'IMAGE_GRID'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.media.MediaPreviewResponseSection':
          return Object.assign({}, section, {
            type: 'MEDIA'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.media.MediaPreviewResponseSectionV2':
          return Object.assign({}, section, {
            type: 'MEDIA_V2'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.LlmResponseSection':
          return Object.assign({}, section, {
            type: 'LLM'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.ReportResponseSection':
          return Object.assign({}, section, {
            type: 'REPORT'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.FilterBranchResponseSection':
          return Object.assign({}, section, {
            type: 'FILTER_BRANCH'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.DenseListResponseSection':
          return Object.assign({}, section, {
            type: 'DENSE_LIST'
          });
        default:
          // TODO: We don't have renderers for:
          // com.hubspot.chatspot.message.schemas.sections.BarChartResponseSection
          // com.hubspot.chatspot.message.schemas.sections.DashboardResponseSection
          throw new Error(`Unknown section type: ${section.__typename}`);
      }
    }
    switch (section.type) {
      case 'USER_CHAT':
        // We don't have a renderer for USER_CHAT so we will map it to PLAIN_TEXT.
        // This is a temporary solution until we have a better way to render it in a V2 Chat component.
        return Object.assign({}, section, {
          type: 'PLAIN_TEXT'
        });
      case 'DATA_TABLE':
        return mapCopilotV2DataTableSectionToDataTableSection(section);
      default:
        return section;
    }
  });
};
export const copilotV2MessageToMessageWithSections = message => {
  const messageWithSections = {
    actions: [],
    id: +message.id,
    query: {
      intent: ''
    },
    response: '',
    source: 'CHAT',
    thread_id: message.threadId,
    time: message.timestamp || Date.now(),
    type: mapCopilotV2MessageTypeToMessageType(message.messageType),
    value: '',
    displayType: SECTIONS,
    sections: mapCopilotV2SectionsToSections(message.sections)
  };
  return messageWithSections;
};